<script setup lang="ts">
const { t: $t } = useI18n()

import type { Prisma } from '@prisma/client'
import type { CustomerModuleCommentCreateOrUpdatePayload, CustomerModuleCommentTypes } from '../../types'

const props = defineProps<{
  type: CustomerModuleCommentTypes
  entityId: string
  title?: string
  createLabel?: string
  isDeleteDisabled?: boolean
  sort?: Prisma.SortOrder
  isSearchable?: boolean
  allowAccessAllPublicComments?: boolean
}>()

const fulltextSearch = props.isSearchable
  ? useFilterData({
      filters: ['/text', '/originalComment/text', '/user/name', '/files/some/path', '/subject', '/replyComments/some/text', '/replyComments/some/subject'],
    }, 200, 3)
  : undefined

const { commentQuery } = useCustomerModuleQuery()
const { data } = commentQuery.getAll(computed(() => ({
  type: props.type,
  entityId: props.entityId,
  sort: props.sort,
  allowAccessAllPublicComments: props.allowAccessAllPublicComments,
})), fulltextSearch?.where)

/** Mutations */
const { $trpc, queryClient, useMutation, makeTrpcErrorToast } = useMutationHelpers()
const notification = useNotification()

const create = useMutation({
  mutationFn: $trpc.customerModule.comment.create.mutate,
  onError: makeTrpcErrorToast(notification, { description: $t('customerModule.comment.notification.create.error') }),
  onSuccess: async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: [CUSTOMER_MODULE_QUERY_KEY, 'comments', 'getAll'] }),
      queryClient.invalidateQueries({ queryKey: [CUSTOMER_MODULE_QUERY_KEY, 'comments', 'getCount'] }),
    ])
    notification.success({ title: $t('customerModule.comment.notification.create.success'), duration: 4500 })
  },
})
const deleteComment = useMutation({
  mutationFn: $trpc.customerModule.comment.delete.mutate,
  onError: makeTrpcErrorToast(notification, { description: $t('customerModule.comment.notification.delete.error') }),
  onSuccess: async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: [CUSTOMER_MODULE_QUERY_KEY, 'comments', 'getAll'] }),
      queryClient.invalidateQueries({ queryKey: [CUSTOMER_MODULE_QUERY_KEY, 'comments', 'getCount'] }),
    ])
    notification.success({ title: $t('customerModule.comment.notification.delete.success'), duration: 4500 })
  },
})
const commentReactionUpsert = useMutation({
  mutationFn: $trpc.customerModule.commentReaction.upsert.mutate,
  onError: makeTrpcErrorToast(notification, { description: $t('customerModule.commentReaction.update.error') }),
  onSuccess: async () => {
    await queryClient.invalidateQueries({ queryKey: [CUSTOMER_MODULE_QUERY_KEY, 'comments', 'getAll'] })
  },
})
const commentReactionDelete = useMutation({
  mutationFn: $trpc.customerModule.commentReaction.deleteByCommentId.mutate,
  onError: makeTrpcErrorToast(notification, { description: $t('customerModule.commentReaction.update.error') }),
  onSuccess: async () => {
    await queryClient.invalidateQueries({ queryKey: [CUSTOMER_MODULE_QUERY_KEY, 'comments', 'getAll'] })
  },
})

const commentCreateOrUpdatePayload = ref<CustomerModuleCommentCreateOrUpdatePayload | undefined>(undefined)
const commentToDeleteId = ref<string | undefined>(undefined)
</script>

<template>
  <div class="flex flex-col md:flex-row md:items-center justify-between gap-2 p-5 border rounded">
    <div class="flex gap-2 items-center">
      <h1 class="text-hos-blue font-semibold text-2xl">
        {{ title ?? $t('customerModule.offer.details.title.comment') }}
      </h1>
      <slot name="title-extra" />
    </div>

    <n-button
      type="primary"
      size="large"
      @click="commentCreateOrUpdatePayload = { mode: 'create', type, entityId }"
    >
      <template #icon>
        <Icon name="material-symbols:add-box-outline-rounded" />
      </template>
      {{ createLabel ?? $t('customerModule.comment.create') }}
    </n-button>
  </div>

  <label v-if="fulltextSearch" class="flex items-center gap-2 mt-4">
    <p class="text-hos-blue font-semibold mb-1">
      {{ $t('comment.search.label') }}:
    </p>
    <n-input
      v-model:value="fulltextSearch.fulltextSearchValue.value"
      filterable
      clearable
      :placeholder="$t('comment.search.placeholder')"
      size="large"
    />
  </label>

  <div class="space-y-4 mt-3">
    <CustomerModuleEmptyPlaceholder
      v-if="!data || data?.length === 0"
      :title="$t('customerModule.comment.empty.title')"
      icon-name="material-symbols:mode-comment-outline-rounded"
    />
    <CommentItem
      v-for="comment in data"
      v-else
      :key="comment.id"
      :comment="comment"
      :entity-id
      :is-delete-disabled="isDeleteDisabled ?? false"
      :is-visibility-adjustable="false"
      :is-read-only="false"
      @delete="commentToDeleteId = $event"
      @create="create.mutate"
      @update="commentCreateOrUpdatePayload = { mode: 'update', type, entityId, id: $event }"
      @reaction-upsert="(commentId, type) => commentReactionUpsert.mutate({ commentId, type })"
      @reaction-delete="commentReactionDelete.mutate({ commentId: $event })"
    />
  </div>

  <CustomerModuleCommentContainer
    v-if="commentCreateOrUpdatePayload"
    :payload="commentCreateOrUpdatePayload"
    width="1100px"
    @close="commentCreateOrUpdatePayload = undefined"
  />
  <TheConfirmPopup
    v-if="commentToDeleteId"
    @confirm="deleteComment.mutate({ id: commentToDeleteId })"
    @close="commentToDeleteId = undefined"
  >
    {{ $t('customerModule.comment.delete.confirm') }}
  </TheConfirmPopup>
</template>
